import React from "react";
import { ArrowRight, Heart, Users, Coffee, Lightbulb, Target, Globe, BookOpen, Award, Zap, CheckCircle2, Compass, Workflow } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { mock } from "@/mock";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Reveal } from "@/components/site/SiteLayout";
import { Card, CardContent } from "@/components/ui/card";

function Glass({ className, children }) {
  return (
    <div
      className={cn(
        "glass rounded-3xl shadow-[0_18px_50px_rgba(0,0,0,0.4)]",
        "transition-shadow duration-200 hover:shadow-[0_0_26px_rgba(0,194,255,0.3)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export default function Culture() {
  const navigate = useNavigate();

  const cultureValues = [
    {
      icon: <Heart className="h-6 w-6 text-primary" />,
      title: "People First",
      description: "We prioritize our team's well-being, growth, and happiness. A positive work environment leads to exceptional results.",
      color: "from-red-500/20 to-pink-500/20"
    },
    {
      icon: <Lightbulb className="h-6 w-6 text-primary" />,
      title: "Innovation",
      description: "We encourage creative thinking and new ideas. Innovation drives us to find better solutions for our clients.",
      color: "from-yellow-500/20 to-orange-500/20"
    },
    {
      icon: <Users className="h-6 w-6 text-primary" />,
      title: "Collaboration",
      description: "We work together as one team. Diverse perspectives and open communication make us stronger.",
      color: "from-blue-500/20 to-cyan-500/20"
    },
    {
      icon: <Target className="h-6 w-6 text-primary" />,
      title: "Excellence",
      description: "We strive for perfection in everything we do. Quality is not just a goal, it's our standard.",
      color: "from-green-500/20 to-emerald-500/20"
    },
    {
      icon: <Globe className="h-6 w-6 text-primary" />,
      title: "Global Mindset",
      description: "We embrace diversity and think globally. Our remote-first approach connects talent worldwide.",
      color: "from-purple-500/20 to-violet-500/20"
    },
    {
      icon: <BookOpen className="h-6 w-6 text-primary" />,
      title: "Continuous Learning",
      description: "We invest in growth and development. Learning never stops, and we support every team member's journey.",
      color: "from-indigo-500/20 to-blue-500/20"
    }
  ];

  // Company values 
  const companyValues = [
    {
      icon: <Award className="h-5 w-5" />,
      title: "Quality First",
      desc: (
        <>
          We never compromise on code quality or user experience.<br />
          <span className="block mt-2">Every line of code and every design decision is made with excellence in mind, ensuring our clients receive products that are robust, reliable, and delightful to use.</span>
        </>
      ),
    },
    {
      icon: <Target className="h-5 w-5" />,
      title: "Client Focus",
      desc: (
        <>
          Your success is our success. We're partners, not vendors.<br />
          <span className="block mt-2">We listen, adapt, and collaborate closely with our clients to understand their needs and deliver solutions that truly make a difference.</span>
        </>
      ),
    },
    {
      icon: <Zap className="h-5 w-5" />,
      title: "Innovation",
      desc: (
        <>
          We stay current with technology to deliver modern solutions.<br />
          <span className="block mt-2">Our team is always exploring new tools, frameworks, and ideas to keep our work fresh, relevant, and ahead of the curve.</span>
        </>
      ),
    },
    {
      icon: <Users className="h-5 w-5" />,
      title: "Transparency",
      desc: (
        <>
          Clear communication and honest updates at every stage.<br />
          <span className="block mt-2">We believe in building trust through openness, sharing progress, challenges, and wins with our clients and team members alike.</span>
        </>
      ),
    },
    {
      icon: <ArrowRight className="h-5 w-5" />,
      title: "Pragmatism",
      desc: (
        <>
          We focus on practical solutions that deliver real value, not just theoretical ideals.<br />
          <span className="block mt-2">We cut through complexity and prioritize what works, making sure our solutions are effective and sustainable in the real world.</span>
        </>
      ),
    },
    {
      icon: <CheckCircle2 className="h-5 w-5" />,
      title: "Ownership",
      desc: (
        <>
          We take responsibility for our work and outcomes, acting as true owners in every project.<br />
          <span className="block mt-2">We are proactive, accountable, and committed to seeing things through, no matter the challenge.</span>
        </>
      ),
    },
  ];

  const benefits = [
    {
      category: "Work-Life Balance",
      items: [
        "Flexible working hours",
        "Remote-first approach",
        "Unlimited PTO policy",
        "Mental health support"
      ]
    },
    {
      category: "Growth & Development",
      items: [
        "Learning & development budget",
        "Conference attendance",
        "Skill certification programs",
        "Mentorship opportunities"
      ]
    },
    {
      category: "Compensation & Benefits",
      items: [
        "Competitive salaries",
        "Performance bonuses",
        "Health insurance",
        "Equipment allowance"
      ]
    }
  ];

  return (
    <div className="space-y-20">
      {/* Hero Section */}
      <section className="relative">
        <div className="w-full px-5 pt-10 md:pt-16 xl:px-10 2xl:px-14">
          <Reveal>
            <div className="max-w-2xl ml-0 mr-auto text-left">
              <Badge className="mb-6 bg-primary/10 text-primary border-primary/20">
                Our Culture
              </Badge>
              <h1 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">
                Where Innovation Meets <span className="text-primary">Collaboration</span>
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-white/70 max-w-2xl">
                At Shiveda IT Services, we've built a culture that celebrates creativity,
                values diversity, and empowers every team member to do their best work.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate("/career")}
                  className="rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow"
                >
                  Join Our Team
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
                  variant="secondary"
                  className="rounded-xl bg-white/10 text-white border-white/20 hover:bg-white/15"
                >
                  Get a Quote
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Who We Are Section */}
      <section>
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal delay={100}>
            <div className="w-full">
              <Glass className="p-8 w-full">
                <div className="grid lg:grid-cols-2 gap-8">
                  <div>
                    <div className="text-xs tracking-[0.22em] text-white/55">WHO WE ARE</div>
                    <div className="mt-3 text-3xl font-semibold tracking-tight text-white">A team built on expertise and passion</div>
                    <p className="mt-4 text-base leading-relaxed text-white/70">
                      We're a collective of developers, designers, and strategists who believe that great software starts with clear thinking. From initial concept to final deployment, we prioritize quality at every step.
                    </p>
                    <p className="mt-4 text-base leading-relaxed text-white/70">
                      Founded with the vision of bridging the gap between innovative technology and practical business solutions, Shiveda IT Services has grown from a small startup to a trusted partner for companies worldwide. Our journey began with a simple belief: that exceptional software should be accessible to businesses of all sizes.
                    </p>
                    <p className="mt-4 text-base leading-relaxed text-white/70">
                      Today, we operate as a remote-first company, bringing together talent from diverse backgrounds and locations. This global perspective enriches our problem-solving approach and allows us to serve clients across different time zones and markets effectively.
                    </p>

                    <div className="mt-8">
                      <h3 className="text-lg font-semibold text-white mb-4">Our Mission</h3>
                      <p className="text-white/70 leading-relaxed">
                        To empower businesses through thoughtfully crafted technology solutions that drive growth, efficiency, and innovation. We don't just build software—we build partnerships that last.
                      </p>
                    </div>

                    <div className="mt-7 grid gap-3">
                      <div className="flex items-center gap-2 text-sm text-white/75">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        10+ successful projects delivered across countries
                      </div>
                      <div className="flex items-center gap-2 text-sm text-white/75">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        Expertise across multiple industries including healthcare, fintech, and e-commerce
                      </div>
                      <div className="flex items-center gap-2 text-sm text-white/75">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        Remote-first, global mindset with team members in 3+ time zones
                      </div>
                      <div className="flex items-center gap-2 text-sm text-white/75">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        Average project completion rate: 98% on-time delivery
                      </div>
                      <div className="flex items-center gap-2 text-sm text-white/75">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        Client satisfaction score: 4.9/5 stars across all projects
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <div className="grid h-16 w-16 place-items-center rounded-2xl border border-white/10 bg-black/55 mx-auto mb-6">

                    </div>

                    {/* Team Image */}
                    <div className="mb-6" style={{ perspective: '1000px' }}>
                      <div className="relative overflow-hidden rounded-2xl border border-white/10 group transition-shadow duration-500 ease-out hover:shadow-[0_25px_60px_rgba(0,194,255,0.3)]">
                        <img
                          src="/team.png"
                          alt="Shiveda IT Services Team"
                          className="w-full h-auto object-cover"
                          style={{
                            transition: 'transform 0.1s ease-out',
                            transformStyle: 'preserve-3d',
                            willChange: 'transform'
                          }}
                          onMouseMove={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const y = e.clientY - rect.top;
                            const centerX = rect.width / 2;
                            const centerY = rect.height / 2;
                            const rotateX = ((y - centerY) / centerY) * 8;
                            const rotateY = ((x - centerX) / centerX) * -8;
                            e.currentTarget.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.03, 1.03, 1.03)`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
                          }}
                        />
                      </div>
                    </div>

                    <div className="bg-white/5 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-3">What Sets Us Apart</h3>
                      <div className="space-y-2 text-sm text-white/70">
                        <p>• <strong className="text-white">Agile Methodology:</strong> Flexible, iterative development process</p>
                        <p>• <strong className="text-white">24/7 Support:</strong> Round-the-clock assistance across time zones</p>
                        <p>• <strong className="text-white">Cutting-Edge Tech:</strong> Latest frameworks and technologies</p>
                        <p>• <strong className="text-white">Custom Solutions:</strong> Tailored to your specific business needs</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Glass>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What Drives Us - Company Values */}
      <section>
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal delay={150}>
            <div className="w-full">
              <div className="text-center mb-10">
                <div className="text-xs tracking-[0.22em] text-white/55">OUR VALUES</div>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white md:text-3xl">
                  What Drives Us
                </h2>
                <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-white/70">
                  Our core values shape every project, partnership, and decision. They define how we work, how we treat each other, and how we deliver for our clients.
                </p>
              </div>
              <div className="border border-white/10 rounded-2xl bg-white/[0.04] shadow p-6 w-full">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {companyValues.map((v) => {
                    const highlight = ["Continuous Learning", "Curiosity", "Humanity"].includes(v.title);
                    return (
                      <Glass
                        key={v.title}
                        className={
                          highlight
                            ? "p-8 min-h-[220px] flex flex-col justify-center items-center border-2 border-primary bg-gradient-to-br from-primary/10 to-black/60 shadow-lg"
                            : "p-8 min-h-[220px] flex flex-col justify-center items-center"
                        }
                      >
                        <div className={highlight
                          ? "grid h-14 w-14 place-items-center rounded-2xl border-2 border-primary bg-primary/20"
                          : "grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-black/55"
                        }>
                          <div className={highlight ? "text-primary" : "text-primary"}>{v.icon}</div>
                        </div>
                        <div className={highlight ? "mt-4 text-lg font-bold text-primary" : "mt-4 text-lg font-semibold text-white"}>
                          {v.title}
                        </div>
                        <p className={highlight ? "mt-3 text-base text-primary text-center" : "mt-3 text-base text-white/70 text-center"}>{v.desc}</p>
                      </Glass>
                    );
                  })}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Where talent thrives */}
      <section>
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal delay={200}>
            <div className="w-full">
              <div className="text-center mb-10">
                <div className="text-xs tracking-[0.22em] text-white/55">OUR CULTURE</div>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white md:text-3xl">
                  Where talent thrives
                </h2>
                <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-white/70">
                  We've built a culture that empowers people to do their best work while maintaining balance and growth
                </p>
              </div>

              {/* Masonry-style layout with varied card positioning */}
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {/* Remote-First - Spans 2 columns on large screens */}
                <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-8 backdrop-blur-sm transition-all duration-300 hover:border-primary/40 hover:shadow-[0_8px_30px_rgba(0,194,255,0.15)] lg:col-span-2">
                  <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-primary/5 blur-2xl transition-all duration-300 group-hover:bg-primary/10" />
                  <div className="relative flex flex-col md:flex-row md:items-start md:gap-6">
                    <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10">
                      <Compass className="h-7 w-7 text-primary" />
                    </div>
                    <div className="mt-5 md:mt-0 flex-1">
                      <h3 className="text-xl font-semibold text-white">Remote-First</h3>
                      <p className="mt-3 text-sm leading-relaxed text-white/60">
                        Work from anywhere in the world. We believe great work happens when people have the freedom to choose their environment and schedule. Our distributed team spans multiple time zones, bringing diverse perspectives to every project.<br />
                        <span className="block mt-3">
                          <strong className="text-white">No Commute, More Life:</strong> Skip the daily commute and reclaim your time for what matters most—family, hobbies, and personal growth.
                        </span>
                        <span className="block mt-2">
                          <strong className="text-white">Collaboration Without Borders:</strong> We use the latest remote tools to keep everyone connected, engaged, and productive, no matter where they are.
                        </span>
                        <span className="block mt-2">
                          <strong className="text-white">Results Over Hours:</strong> We focus on outcomes, not clock-watching. Flexible schedules empower you to work when you're at your best.
                        </span>
                        <span className="block mt-2">
                          <strong className="text-white">Supportive Remote Culture:</strong> Regular virtual team events, open communication channels, and a strong sense of belonging—distance never means isolation here.
                        </span>
                      </p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                          Global Team
                        </span>
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                          Flexible Hours
                        </span>
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                          Async First
                        </span>
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                          No Commute
                        </span>
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                          Virtual Events
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Continuous Learning - Enhanced with animation */}
                <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-6 backdrop-blur-sm transition-all duration-300 hover:border-primary/40 hover:shadow-[0_8px_30px_rgba(0,194,255,0.15)] min-h-[280px] flex flex-col">
                  {/* Animated background elements */}
                  <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-primary/5 blur-2xl transition-all duration-300 group-hover:bg-primary/10 group-hover:scale-150" />
                  <div className="absolute -left-4 top-1/2 h-16 w-16 rounded-full bg-primary/5 blur-xl transition-all duration-500 group-hover:translate-x-2 group-hover:scale-110" />

                  {/* Floating animated circles */}
                  <div className="absolute top-12 right-12 h-2 w-2 rounded-full bg-primary/30 animate-pulse" />
                  <div className="absolute bottom-20 left-8 h-3 w-3 rounded-full bg-primary/20" style={{ animation: 'bounce 3s infinite ease-in-out' }} />

                  <div className="relative flex flex-col h-full">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                      <Target className="h-6 w-6 text-primary transition-transform duration-300 group-hover:scale-110" />
                    </div>

                    <h3 className="mt-5 text-lg font-semibold text-white transition-colors duration-300 group-hover:text-primary">Continuous Learning</h3>

                    <p className="mt-3 text-sm leading-relaxed text-white/60 flex-1">
                      Annual learning budget, conference attendance, and dedicated time for skill development. We invest in your growth because we know that continuous improvement is the key to innovation and excellence.
                    </p>

                    {/* Progress bar animation */}
                    <div className="mt-4 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/50">Skill Growth</span>
                        <span className="text-xs text-primary font-medium">95%</span>
                      </div>
                      <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-primary/70 to-primary rounded-full transition-all duration-1000 group-hover:w-full" style={{ width: '70%' }} />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-auto">
                      <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary transition-all duration-300 hover:bg-primary/10 hover:scale-105">
                        Training Budget
                      </span>
                      <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary transition-all duration-300 hover:bg-primary/10 hover:scale-105">
                        Conferences
                      </span>
                      <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary transition-all duration-300 hover:bg-primary/10 hover:scale-105">
                        Workshops
                      </span>
                    </div>
                  </div>
                </div>

                {/* Innovation Time */}
                <div className="flex flex-col lg:flex-row gap-5 lg:col-span-3">
                  {/* Innovation Time */}
                  <div className="group relative flex-1 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-6 backdrop-blur-sm transition-all duration-300 hover:border-primary/40 hover:shadow-[0_8px_30px_rgba(0,194,255,0.15)] min-h-[280px] flex flex-col">
                    <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-primary/5 blur-2xl transition-all duration-300 group-hover:bg-primary/10" />
                    <div className="relative flex flex-col h-full">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-primary/20 bg-primary/10">
                        <Zap className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="mt-5 text-lg font-semibold text-white">Innovation Time</h3>
                      <p className="mt-2 text-sm leading-relaxed text-white/60 flex-1">
                        20% time for side projects and experimentation. Your passion projects might become our next big feature.
                      </p>
                      <div className="mt-auto flex flex-wrap gap-2">
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                          Side Projects
                        </span>
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                          Hackathons
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Open Communication */}
                  <div className="group relative flex-1 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-8 backdrop-blur-sm transition-all duration-300 hover:border-primary/40 hover:shadow-[0_8px_30px_rgba(0,194,255,0.15)] flex flex-col">
                    <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-primary/5 blur-2xl transition-all duration-300 group-hover:bg-primary/10" />
                    <div className="relative flex flex-col md:flex-row md:items-start md:gap-6">
                      <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10">
                        <Workflow className="h-7 w-7 text-primary" />
                      </div>
                      <div className="mt-5 md:mt-0 flex-1">
                        <h3 className="text-xl font-semibold text-white">Open Communication</h3>
                        <p className="mt-3 text-sm leading-relaxed text-white/60">
                          Transparent decision-making, regular town halls, and direct access to leadership. Everyone's voice matters here. We foster an environment where ideas flow freely and feedback is not just welcomed but actively sought.
                        </p>
                        <div className="mt-5 flex flex-wrap gap-2">
                          <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                            Town Halls
                          </span>
                          <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                            Open Door
                          </span>
                          <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                            360° Feedback
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits & Perks */}
      <section>
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal delay={400}>
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Benefits & Perks</h2>
              <p className="mt-4 text-lg text-white/70">
                We take care of our team so they can take care of our clients
              </p>
            </div>
          </Reveal>

          <div className="grid gap-8 lg:grid-cols-3">
            {benefits.map((benefit, index) => (
              <Reveal key={benefit.category} delay={500 + index * 100}>
                <Glass className="p-8">
                  <h3 className="text-xl font-semibold mb-6 text-primary">
                    {benefit.category}
                  </h3>
                  <ul className="space-y-3">
                    {benefit.items.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <Coffee className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-white/80">{item}</span>
                      </li>
                    ))}
                  </ul>
                </Glass>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Remote Culture */}
      <section>
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal delay={600}>
            <Glass className="p-8 lg:p-12">
              <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
                <div>
                  <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                    Remote-First
                  </Badge>
                  <h2 className="text-3xl font-bold tracking-tight mb-6">
                    Global Team, Local Impact
                  </h2>
                  <p className="text-lg text-white/70 leading-relaxed mb-8">
                    Our remote-first culture enables us to attract the best talent from around the world.
                    We've built systems and processes that ensure seamless collaboration across time zones,
                    maintaining the close-knit feel of a local team with the diversity of a global one.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                      <div>
                        <div className="font-medium">Async Communication</div>
                        <div className="text-sm text-white/60">Thoughtful communication that respects everyone's time</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                      <div>
                        <div className="font-medium">Flexible Hours</div>
                        <div className="text-sm text-white/60">Work when you're most productive</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                      <div>
                        <div className="font-medium">Virtual Team Building</div>
                        <div className="text-sm text-white/60">Regular events that bring our distributed team together</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <div className="aspect-square rounded-2xl bg-gradient-to-br from-primary/20 to-blue-500/20 p-8 flex items-center justify-center">
                    <div className="text-center">
                      <Globe className="h-16 w-16 text-primary mx-auto mb-4" />
                      <div className="text-2xl font-bold">15+</div>
                      <div className="text-white/70">Countries</div>
                      <div className="mt-4 text-lg font-semibold">One Team</div>
                    </div>
                  </div>
                </div>
              </div>
            </Glass>
          </Reveal>
        </div>
      </section>

      {/* CTA Section */}
      <section>
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal delay={700}>
            <Glass className="p-8 lg:p-12 text-center">
              <h2 className="text-3xl font-bold tracking-tight mb-4">
                Ready to Join Our Team?
              </h2>
              <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                We're always looking for talented individuals who share our values and passion for excellence.
                Discover opportunities to grow your career with us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
                  className="rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow"
                >
                  View Open Positions
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  onClick={() => navigate("/contact")}
                  variant="secondary"
                  className="rounded-xl bg-white/10 text-white border-white/20 hover:bg-white/15"
                >
                  Get in Touch
                </Button>
              </div>
            </Glass>
          </Reveal>
        </div>
      </section>
    </div>
  );
}